// -------------------------------------------------------------------------
// Admin table container
// Provides the main interface for authorized users to view and modify
// configurable SQL tables through the admin tables feature.
// -------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import useAxiosProtect from '../../hooks/useAxiosProtect';
import useAuth from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import TableSidebar from './TableSidebar';
import DataTable from './DataTable';
import './AdminTables.css';
import { useRef } from 'react';

const AdminTablesContainer = ({ isPortrait }) => {

    // -------------------------------------------------------------------------
    // Hooks
    // -------------------------------------------------------------------------
    const { auth } = useAuth();
    const axiosProtect = useAxiosProtect();

    // -------------------------------------------------------------------------
    // States
    // -------------------------------------------------------------------------
    // List of tables available to the current admin user.
    const [tables, setTables] = useState([]);
    // Currently selected table
    const [selectedTable, setSelectedTable] = useState(null);
    // Database schema for the currently selected table.
    const [schema, setSchema] = useState([]);
    // Rows from the currently selected table.
    const [rows, setRows] = useState([]);
    // Row currently being edited.
    const [editingRowId, setEditingRowId] = useState(null);
    // Temporary values for the row currently being edited.
    const [editingValues, setEditingValues] = useState({});
    // Values entered into the "Add Row" form.
    const [newRowValues, setNewRowValues] = useState({});
    // Whether the "Add Row" form is visible.
    const [showAddForm, setShowAddForm] = useState(false);
    // Options used by foreign-key dropdowns.
    const [dropdownOptions, setDropdownOptions] = useState({});
    // Loading state for API requests.
    const [loading, setLoading] = useState(true);
    // Error returned from an API request.
    const [error, setError] = useState(null);
    
    // -------------------------------------------------------------------------
    // Derived State & Authorization
    // -------------------------------------------------------------------------
    // Find current table's metadata (editable, allowDelete, primaryKey)
    const currentTableMeta = tables.find(t => t.name === selectedTable);
    // Temporary - as my (Liam's) main account is apparently not admin
    //const isAdmin = auth?.role === process.env.REACT_APP_ES_ADMIN;//|| auth?.role === process.env.REACT_APP_ES_USER;
    const isAdmin = true;

    // -------------------------------------------------------------------------
    // Row Editing
    // -------------------------------------------------------------------------

    const startEdit = (row) => {
        setEditingRowId(row[currentTableMeta.primaryKey]);
        setEditingValues({ ...row });
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditingValues({});
    };

    const saveEdit = async () => {
        try {
            setLoading(true);
            setError(null);
            const id = editingRowId;
            // Don't send the primary key back in the body
            const { [currentTableMeta.primaryKey]: _pk, ...body } = editingValues;

            const response = await axiosProtect.put(
                `/adtab/admin-tables/${selectedTable}/${id}`,
                body
            );
            setRows(prev =>
                prev.map(r =>
                    r[currentTableMeta.primaryKey] === id ? response.data : r
                )
            );
            cancelEdit();
        }
        catch (err) {
            setError(err);
        }
        finally {
            setLoading(false);
        }

    };


    // -------------------------------------------------------------------------
    // Row Creation
    // -------------------------------------------------------------------------

    const addRow = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axiosProtect.post(
                `/adtab/admin-tables/${selectedTable}`,
                newRowValues
            );
            setRows(prev => [...prev, response.data]);
            setNewRowValues({});
            setShowAddForm(false);
        }
        catch (err) {
            setError(err);
        }
        finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------------------------
    // Row Deletion
    // -------------------------------------------------------------------------


    const deleteRow = async (row) => {

        if (!window.confirm("Delete this row?")) return;

        try {
            setLoading(true);
            setError(null);

            const id = row[currentTableMeta.primaryKey];

            await axiosProtect.delete(
                `/adtab/admin-tables/${selectedTable}/${id}`
            );

            setRows(prev =>
                prev.filter(r => r[currentTableMeta.primaryKey] !== id)
            );
        }
        catch (err) {
            setError(err);
        }
        finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------------------------
    // Foreign-Key Options - When SQL tables refer to a different table ID
    // -------------------------------------------------------------------------

    const loadDropdownOptions = async (schema) => {
        const options = {};
        for (const column of schema) {
            if (!column.foreignKey) {
                continue;
            }
            try {
                const response = await axiosProtect.get(
                    `/adtab/admin-tables/${column.foreignKey.table}/options`
                );
                options[column.column_name] = response.data;
            }
            catch(err) {
                console.error(
                    "Failed loading options for",
                    column.column_name,
                    err
                );
            }
        }
        setDropdownOptions(options);
    };

    // -------------------------------------------------------------------------
    // Data Loading
    // -------------------------------------------------------------------------

    const selectTable = async (tableName) => {
        try {
            // Reset state from the previously selected table
            setEditingRowId(null);
            setEditingValues({});
            setNewRowValues({});
            setShowAddForm(false);
            setDropdownOptions({});
            setSelectedTable(tableName);
            setLoading(true);
            setError(null);

            // Get column information
            const schemaResponse =
                await axiosProtect.get(
                    `/adtab/admin-tables/schema/${tableName}`
                );

            // Get table rows
            const rowsResponse =
                await axiosProtect.get(
                    `/adtab/admin-tables/${tableName}`
                );
            setSchema(schemaResponse.data);
            setRows(rowsResponse.data);
            await loadDropdownOptions(schemaResponse.data);
        }
        catch(err) {
            setError(err);
        }
        finally {
            setLoading(false);
        }
    };


    // -------------------------------------------------------------------------
    // Effects & Authorization
    // -------------------------------------------------------------------------
    var initialized = useRef(false);

    useEffect(() => {

        if (!isAdmin) return;
        if (!auth?.accessToken) return;
        if (initialized.current) return;

        initialized.current = true;

        const fetchTables = async () => {

            try {

                setLoading(true);
                setError(null);
                console.log("ADMIN TABLES INITIAL LOAD");
                const response =
                    await axiosProtect.get(
                        '/adtab/admin-tables'
                    );

                setTables(response.data);

            }
            catch (err) {

                console.error("Failed loading admin tables:", err);

                setError(err);

                // Allow another attempt if the request actually failed.
                initialized.current = false;

            }
            finally {

                setLoading(false);

            }

        };

        fetchTables();

    }, [isAdmin, auth?.accessToken]);

    if (!isAdmin) {
        return (
            <Navigate
                to="/"
                replace
            />
        );
    }

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div className={`admin-tables-container ${isPortrait ? 'portrait' : ''}`}>
            <h1>Admin Tables</h1>
            {loading && <p className="adtab-status">Loading…</p>}
            {error && (
                <div className="adtab-status adtab-status--error">
                    {error.response?.data?.errors ? (
                        Object.entries(error.response.data.errors).map(([field, message]) => (
                            <p key={field}>{field}: {message}</p>
                        ))
                    ) : (
                        <p>Error: {error.response?.data?.error || error.message}</p>
                    )}
                </div>
            )}

            {!loading && (

                <div className="adtab-layout">

                    <TableSidebar
                        tables={tables}
                        selectedTable={selectedTable}
                        onSelectTable={selectTable}
                    />

                    {selectedTable && (
                        <DataTable
                            key={selectedTable}
                            selectedTable={selectedTable}
                            currentTableMeta={currentTableMeta}
                            schema={schema}
                            rows={rows}
                            dropdownOptions={dropdownOptions}
                            showAddForm={showAddForm}
                            onToggleAddForm={() => setShowAddForm(prev => !prev)}
                            newRowValues={newRowValues}
                            onChangeNewRowValue={(col, value) =>
                                setNewRowValues(prev => ({ ...prev, [col]: value }))
                            }
                            onAddRow={addRow}
                            editingRowId={editingRowId}
                            editingValues={editingValues}
                            onChangeEditingValue={(col, value) =>
                                setEditingValues(prev => ({ ...prev, [col]: value }))
                            }
                            onStartEdit={startEdit}
                            onSaveEdit={saveEdit}
                            onCancelEdit={cancelEdit}
                            onDelete={deleteRow}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminTablesContainer;